import React, { useState, useEffect, useRef } from 'react'
import { Plus, Search, Edit, Trash2, Clock, IndianRupee, CheckCircle, XCircle, Calendar, User, Truck, FileText, FileBarChart, Download, ChevronDown } from 'lucide-react'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useData } from '../context/DataContext'
import WorkEntryForm from '../components/WorkEntryForm'
import InvoiceGenerator from '../components/InvoiceGenerator'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'

function WorkManagement() {
  const { state, addWorkEntry, updateWorkEntry, deleteWorkEntry, addPayment } = useData()
  const { workEntries, customers, vehicles, drivers } = state

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterCustomer, setFilterCustomer] = useState('all')
  const [filterVehicle, setFilterVehicle] = useState('all')
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false)
  const customerDropdownRef = useRef(null)

  // Handle click outside to close customer dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false)
        setCustomerSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const [invoiceEntry, setInvoiceEntry] = useState(null)
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false)
  const [statementStartDate, setStatementStartDate] = useState('')
  const [statementEndDate, setStatementEndDate] = useState('')
  const [statementPaymentStatus, setStatementPaymentStatus] = useState('all')
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  // Helper function to calculate actual payment status based on amounts
  const calculatePaymentStatus = (entry) => {
    const baseAmount = parseFloat(entry.amount || 0)
    const betaAmount = entry.workItems ? entry.workItems.reduce((sum, item) => 
      sum + parseFloat(item.betaAmount || 0), 0) : 0
    const taxAmount = parseFloat(entry.taxAmount || 0)
    const totalAmount = baseAmount + betaAmount + taxAmount
    const paidAmount = parseFloat(entry.paidAmount || 0)
    
    // Check if fully paid first
    if (totalAmount > 0 && Math.abs(paidAmount - totalAmount) < 0.01) {
      return 'paid'
    }
    // Check if partially paid
    if (paidAmount > 0 && paidAmount < totalAmount) {
      return 'partial'
    }
    // Default to pending
    return 'pending'
  }

  const filteredEntries = workEntries.filter(entry => {
    const customer = customers.find(c => c.id === entry.customerId)
    const vehicle = vehicles.find(v => v.id === entry.vehicleId)
    const driver = drivers.find(d => d.id === entry.driverId)
    
    // Improved search logic - search in multiple fields and handle empty search
    let matchesSearch = true
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      const customerName = (customer?.name || '').toLowerCase()
      const vehicleNumber = (vehicle?.registrationNumber || '').toLowerCase()
      const driverName = (driver?.name || '').toLowerCase()
      const location = (entry.location || '').toLowerCase()
      const workDescription = (entry.workDescription || '').toLowerCase()
      const invoiceNumber = (entry.invoiceNumber || '').toLowerCase()
      
      // Search in multiple fields
      matchesSearch = 
        customerName.includes(searchLower) ||
        vehicleNumber.includes(searchLower) ||
        driverName.includes(searchLower) ||
        location.includes(searchLower) ||
        workDescription.includes(searchLower) ||
        invoiceNumber.includes(searchLower) ||
        // Search for individual words
        searchLower.split(' ').some(word => 
          customerName.includes(word) ||
          vehicleNumber.includes(word) ||
          driverName.includes(word) ||
          location.includes(word) ||
          workDescription.includes(word) ||
          invoiceNumber.includes(word)
        )
    }
    
    const actualPaymentStatus = calculatePaymentStatus(entry)
    const matchesStatus = filterStatus === 'all' || actualPaymentStatus === filterStatus
    const matchesCustomer = filterCustomer === 'all' || entry.customerId === filterCustomer
    const matchesVehicle = filterVehicle === 'all' || 
      entry.vehicleId === filterVehicle || 
      (entry.workItems && entry.workItems.some(item => item.vehicleId === filterVehicle))

    
    // Date range filtering
    let matchesDateRange = true
    if (startDate || endDate) {
      const entryDate = new Date(entry.workDate)
      const start = startDate ? new Date(startDate) : null
      const end = endDate ? new Date(endDate) : null
      
      if (start && end) {
        matchesDateRange = entryDate >= start && entryDate <= end
      } else if (start) {
        matchesDateRange = entryDate >= start
      } else if (end) {
        matchesDateRange = entryDate <= end
      }
    }
    
    return matchesSearch && matchesStatus && matchesCustomer && matchesVehicle && matchesDateRange
  })

  const handleAddEntry = () => {
    setEditingEntry(null)
    setIsModalOpen(true)
  }

  const handleEditEntry = (entry) => {
    setEditingEntry(entry)
    setIsModalOpen(true)
  }

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, entry: null })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleDeleteEntry = (entry) => {
    const customer = customers.find(c => c.id === entry.customerId)
    if (window.confirm(`Are you sure you want to delete this work entry for ${customer?.name}?`)) {
      deleteWorkEntry(entry.id)
    }
  }

  const openDeleteModal = (entry) => {
    setDeleteModal({ isOpen: true, entry })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, entry: null })
  }

  const confirmDelete = () => {
    if (deleteModal.entry) {
      deleteWorkEntry(deleteModal.entry.id)
    }
  }

  const handleSubmit = async (entryData) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate request')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      if (editingEntry) {
        const result = await updateWorkEntry(editingEntry.id, entryData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingEntry(null)
        } else {
          alert(`Error updating work entry: ${result.error}`)
        }
      } else {
        const result = await addWorkEntry(entryData)
        if (result.success) {
          // If there's a paid amount, add it as payment
          const paidAmount = entryData.paidAmount || entryData.initialPayment
          if (paidAmount && parseFloat(paidAmount) > 0) {
            const paymentData = {
              amount: paidAmount,
              date: new Date().toISOString(),
              method: entryData.paymentMethod || 'cash',
              notes: entryData.paymentNotes || 'Initial payment'
            }
            
            // Handle payment separately - don't let payment failure affect work entry success
            try {
              const paymentResult = await addPayment(result.data.id, paymentData)
              if (!paymentResult.success) {
                console.warn('Payment could not be added:', paymentResult.error)
                // Show a warning but don't fail the entire operation
                alert(`Work entry saved successfully, but payment could not be recorded: ${paymentResult.error}`)
              }
            } catch (paymentError) {
              console.warn('Payment failed:', paymentError)
              // Show a warning but don't fail the entire operation
              alert(`Work entry saved successfully, but payment could not be recorded: ${paymentError.message}`)
            }
          }
          
          // Always close modal and clear editing state since work entry was successful
          setIsModalOpen(false)
          setEditingEntry(null)
        } else {
          alert(`Error creating work entry: ${result.error}`)
        }
      }
    } catch (error) {
      console.error('Error submitting work entry:', error)
      alert('An error occurred while saving the work entry. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingEntry(null)
  }

  const handleGenerateInvoice = (entry) => {
    setInvoiceEntry(entry)
    setIsInvoiceModalOpen(true)
  }

  const handleCloseInvoiceModal = () => {
    setIsInvoiceModalOpen(false)
    setInvoiceEntry(null)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  // Helper function to calculate proportional given amounts ensuring exact total
  const calculateProportionalGivenAmount = (workItem, workEntry) => {
    if (!workEntry || !workEntry.workItems) return 0
    
    const entryPaidAmount = parseFloat(workEntry.paidAmount || 0)
    if (entryPaidAmount <= 0) return 0
    
    const workItems = workEntry.workItems
    const totalWorkItemsAmount = workItems.reduce((sum, item) => {
      const baseAmount = parseFloat(item.amount || 0)
      const betaAmount = parseFloat(item.betaAmount || 0)
      return sum + baseAmount + betaAmount
    }, 0)
    
    if (totalWorkItemsAmount <= 0) return 0
    
    // Calculate this work item's amount
    const workItemBaseAmount = parseFloat(workItem.amount || 0)
    const workItemBetaAmount = parseFloat(workItem.betaAmount || 0)
    const workItemTotalAmount = workItemBaseAmount + workItemBetaAmount
    
    // Calculate proportional amount
    const proportion = workItemTotalAmount / totalWorkItemsAmount
    const proportionalAmount = entryPaidAmount * proportion
    
    // Round to 2 decimal places
    return Math.round(proportionalAmount * 100) / 100
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'partial':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-orange-100 text-orange-800'
    }
  }

  // Calculate summary statistics
  const totalHours = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.hoursWorked || 0), 0)
  const totalBaseAmount = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0)
  const totalBetaAmount = filteredEntries.reduce((sum, entry) => {
    // Calculate betta amount from work items
    const workItemsBeta = entry.workItems ? entry.workItems.reduce((itemSum, item) => 
      itemSum + parseFloat(item.betaAmount || 0), 0) : 0
    return sum + workItemsBeta
  }, 0)
  const totalTaxAmount = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.taxAmount || 0), 0)
  const totalAmount = totalBaseAmount + totalBetaAmount + totalTaxAmount
  const totalPaidAmount = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.paidAmount || 0), 0)
  const pendingAmount = totalAmount - totalPaidAmount
  const totalDriverExpenses = filteredEntries.reduce((sum, entry) => {
    // Calculate driver expenses from work items
    const workItemsDriverExpenses = entry.workItems ? entry.workItems.reduce((itemSum, item) => 
      itemSum + parseFloat(item.driverExpenses || 0), 0) : 0
    return sum + workItemsDriverExpenses
  }, 0)
  const totalDiscountAmount = filteredEntries.reduce((sum, entry) => sum + parseFloat(entry.discountAmount || 0), 0)

  // Calculate customer statement when a specific customer is selected or all customers
  const getCustomerStatement = () => {
    let customerEntries = []
    let customer = null
    
    if (filterCustomer === 'all') {
      // All customers statement
      customerEntries = workEntries
      customer = { name: 'All Customers', company: 'Combined Statement', phone: 'N/A' }
    } else {
      // Specific customer statement
      customerEntries = workEntries.filter(entry => entry.customerId === filterCustomer)
      customer = customers.find(c => c.id === filterCustomer)
      
      if (!customer || customerEntries.length === 0) return null
    }
    
    // Apply date range filter if dates are selected
    if (statementStartDate || statementEndDate) {
      customerEntries = customerEntries.filter(entry => {
        const entryDate = new Date(entry.workDate)
        const start = statementStartDate ? new Date(statementStartDate) : null
        const end = statementEndDate ? new Date(statementEndDate) : null
        
        if (start && end) {
          return entryDate >= start && entryDate <= end
        } else if (start) {
          return entryDate >= start
        } else if (end) {
          return entryDate <= end
        }
        return true
      })
    }
    
    // Apply payment status filter if selected
    if (statementPaymentStatus !== 'all') {
      customerEntries = customerEntries.filter(entry => calculatePaymentStatus(entry) === statementPaymentStatus)
    }
    
    const totalRecords = customerEntries.length
    const totalHours = customerEntries.reduce((sum, entry) => sum + parseFloat(entry.hoursWorked || 0), 0)
    const totalBaseAmount = customerEntries.reduce((sum, entry) => sum + parseFloat(entry.amount || 0), 0)
    const totalBetaAmount = customerEntries.reduce((sum, entry) => {
      const workItemsBeta = entry.workItems ? entry.workItems.reduce((itemSum, item) => 
        itemSum + parseFloat(item.betaAmount || 0), 0) : 0
      return sum + workItemsBeta
    }, 0)
    const totalTaxAmount = customerEntries.reduce((sum, entry) => sum + parseFloat(entry.taxAmount || 0), 0)
    const totalAmount = totalBaseAmount + totalBetaAmount + totalTaxAmount
    const totalPaidAmount = customerEntries.reduce((sum, entry) => sum + parseFloat(entry.paidAmount || 0), 0)
    const pendingAmount = totalAmount - totalPaidAmount
    const totalDriverExpenses = customerEntries.reduce((sum, entry) => {
      const workItemsDriverExpenses = entry.workItems ? entry.workItems.reduce((itemSum, item) => 
        itemSum + parseFloat(item.driverExpenses || 0), 0) : 0
      return sum + workItemsDriverExpenses
    }, 0)
    const totalDiscountAmount = customerEntries.reduce((sum, entry) => sum + parseFloat(entry.discountAmount || 0), 0)
    
    // Get all work items details
    const allWorkItems = customerEntries.flatMap(entry => 
      entry.workItems ? entry.workItems.map(item => ({
        ...item,
        workDate: entry.workDate,
        workDescription: entry.workDescription,
        location: entry.location,
        invoiceNumber: entry.invoiceNumber
      })) : []
    )
    
    return {
      customer,
      totalRecords,
      totalHours,
      totalBaseAmount,
      totalBetaAmount,
      totalTaxAmount,
      totalAmount,
      totalPaidAmount,
      pendingAmount,
      totalDriverExpenses,
      totalDiscountAmount,
      allWorkItems,
      customerEntries
    }
  }

  const customerStatement = getCustomerStatement()

    // Generate PDF for customer statement
  const generatePDF = async (statement) => {
    setIsGeneratingPDF(true)
    try {
      console.log('Starting PDF generation...')
      
      // Check if running on mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      console.log('Is mobile device:', isMobile)
      
      const doc = new jsPDF()
      console.log('jsPDF instance created')
      
      // Set font
      doc.setFont('helvetica')
      
      // Header
      doc.setFontSize(20)
      doc.setTextColor(255, 69, 0) // Orange color (website theme)
      doc.text('Sri Vemurugan Transport and Earthmovers', 105, 20, { align: 'center' })
      
      doc.setFontSize(16)
      doc.setTextColor(0, 0, 0)
      doc.text('Customer Statement', 105, 35, { align: 'center' })
      
      // Add line separator after heading
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.5)
      doc.line(20, 42, 190, 42)
      
      // Date Range Info
      if (statementStartDate || statementEndDate) {
        doc.setFontSize(12)
        doc.setTextColor(128, 128, 128)
        let dateRangeText = 'Period: '
        if (statementStartDate && statementEndDate) {
          dateRangeText += `${new Date(statementStartDate).toLocaleDateString()} to ${new Date(statementEndDate).toLocaleDateString()}`
        } else if (statementStartDate) {
          dateRangeText += `From ${new Date(statementStartDate).toLocaleDateString()}`
        } else if (statementEndDate) {
          dateRangeText += `Until ${new Date(statementEndDate).toLocaleDateString()}`
        }
        doc.text(dateRangeText, 105, 45, { align: 'center' })
      }
      
      // Customer Information
      const startY = statementStartDate || statementEndDate ? 60 : 50
      doc.setFontSize(14)
      doc.setTextColor(255, 69, 0)
      doc.text('Customer Details:', 20, startY)
      
      doc.setFontSize(12)
      doc.setTextColor(0, 0, 0)
      doc.text(`Name: ${statement.customer.name}`, 20, startY + 10)
      doc.text(`Company: ${statement.customer.company || 'N/A'}`, 20, startY + 20)
      doc.text(`Phone: ${statement.customer.phone || 'N/A'}`, 20, startY + 30)
      doc.text(`Email: ${statement.customer.email || 'N/A'}`, 20, startY + 40)
      
      // Add line separator after customer details
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.5)
      doc.line(20, startY + 50, 190, startY + 50)
      
      // Summary Statistics
      const statsStartY = startY + 60
      doc.setFontSize(14)
      doc.setTextColor(255, 69, 0)
      doc.text('Summary Statistics:', 20, statsStartY)
      
      doc.setFontSize(10)
      doc.setTextColor(0, 0, 0)
      doc.text(`Total Records: ${statement.totalRecords}`, 20, statsStartY + 10)
      doc.text(`Total Hours: ${statement.totalHours.toFixed(1)}`, 20, statsStartY + 20)
      doc.text(`Total Amount: ${statement.totalAmount.toFixed(2)}`, 20, statsStartY + 30)
      doc.text(`Given Amount: ${statement.totalPaidAmount.toFixed(2)}`, 20, statsStartY + 40)
      doc.text(`Pending Amount: ${statement.pendingAmount.toFixed(2)}`, 20, statsStartY + 50)
      doc.text(`Base Amount: ${statement.totalBaseAmount.toFixed(2)}`, 20, statsStartY + 60)
      doc.text(`Betta Amount: ${statement.totalBetaAmount.toFixed(2)}`, 20, statsStartY + 70)
      doc.text(`Tax Amount: ${statement.totalTaxAmount.toFixed(2)}`, 20, statsStartY + 80)
      doc.text(`Discount Amount: ${statement.totalDiscountAmount.toFixed(2)}`, 20, statsStartY + 90)
      
      // Add line separator after summary statistics
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.5)
      doc.line(20, statsStartY + 100, 190, statsStartY + 100)
      
      // Work Items Table - Consolidated by Work Entry
      if (statement.customerEntries.length > 0) {
        const tableStartY = statsStartY + 110
        doc.setFontSize(14)
        doc.setTextColor(255, 69, 0)
        doc.text('Work Items Details:', 20, tableStartY)
        
        const tableData = statement.customerEntries.map((entry, index) => {
          const vehicle = vehicles.find(v => v.id === entry.vehicleId)
          
          // Calculate totals for this work entry
          let totalBaseAmount = 0
          let totalBetaAmount = 0
          let totalHours = 0
          let workDescriptions = []
          let dateRange = ''
          
          if (entry.workItems && entry.workItems.length > 0) {
            totalBaseAmount = entry.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
            totalBetaAmount = entry.workItems.reduce((sum, item) => sum + parseFloat(item.betaAmount || 0), 0)
            totalHours = entry.workItems.reduce((sum, item) => sum + parseFloat(item.hoursWorked || 0), 0)
            workDescriptions = entry.workItems.map(item => item.description).filter(desc => desc && desc !== 'N/A')
            
            // Calculate date range from first to last work item
            const workDates = entry.workItems
              .map(item => new Date(item.workDate))
              .filter(date => !isNaN(date.getTime()))
              .sort((a, b) => a - b)
            
            if (workDates.length > 0) {
              const firstDate = workDates[0]
              const lastDate = workDates[workDates.length - 1]
              
              if (firstDate.getTime() === lastDate.getTime()) {
                // Same date
                dateRange = firstDate.toLocaleDateString()
              } else {
                // Different dates - show range
                dateRange = `${firstDate.toLocaleDateString()} - ${lastDate.toLocaleDateString()}`
              }
            } else {
              dateRange = new Date(entry.workDate).toLocaleDateString()
            }
          } else {
            // Fallback for entries without work items
            totalBaseAmount = parseFloat(entry.amount || 0)
            totalHours = parseFloat(entry.hoursWorked || 0)
            workDescriptions = [entry.workDescription || 'Work Entry']
            dateRange = new Date(entry.workDate).toLocaleDateString()
          }
          
          const totalAmount = totalBaseAmount + totalBetaAmount
          const paidAmount = parseFloat(entry.paidAmount || 0)
          
          // Get all vehicle numbers from work items
          let vehicleInfo = 'N/A'
          if (entry.workItems && entry.workItems.length > 0) {
            const vehicleNumbers = entry.workItems
              .map(item => {
                const itemVehicle = vehicles.find(v => v.id === item.vehicleId)
                return itemVehicle ? `${itemVehicle.registrationNumber} - ${itemVehicle.type}` : 'N/A'
              })
              .filter(v => v !== 'N/A')
              .filter((v, index, arr) => arr.indexOf(v) === index) // Remove duplicates
            
            vehicleInfo = vehicleNumbers.length > 0 ? vehicleNumbers.join(', ') : 'N/A'
          } else if (vehicle) {
            vehicleInfo = `${vehicle.registrationNumber} - ${vehicle.type}`
          }
          
          return [
            dateRange,
            workDescriptions.join(', ') || 'N/A',
            entry.location || 'N/A',
            vehicleInfo,
            totalHours.toFixed(1),
            totalBaseAmount.toFixed(2),
            totalBetaAmount.toFixed(2),
            totalAmount.toFixed(2),
            paidAmount.toFixed(2),
            entry.paymentStatusDescription || 'N/A'
          ]
        })
        
        autoTable(doc, {
          startY: tableStartY + 10,
          head: [['Date', 'Description', 'Location', 'Vehicle', 'Hours', 'Base\nAmount', 'Betta\nAmount', 'Total\nAmount', 'Given\nAmount', 'Payment\nStatus\nDescription']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: [255, 69, 0],
            textColor: 255,
            fontSize: 8,
            halign: 'center',
            valign: 'middle',
            lineHeight: 1.2
          },
          bodyStyles: {
            fontSize: 8
          },
          columnStyles: {
            0: { cellWidth: 18 }, // Date
            1: { cellWidth: 25 }, // Description
            2: { cellWidth: 22 }, // Location
            3: { cellWidth: 22 }, // Vehicle
            4: { cellWidth: 12 }, // Hours
            5: { cellWidth: 18 }, // Base Amount
            6: { cellWidth: 18 }, // Betta Amount
            7: { cellWidth: 18 }, // Total Amount
            8: { cellWidth: 18 }, // Given Amount
            9: { cellWidth: 25 }  // Payment Status Description
          },
          margin: { top: 10, left:7, right: 30 }
        })
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(10)
        doc.setTextColor(128, 128, 128)
        doc.text(`Page ${i} of ${pageCount}`, 105, doc.internal.pageSize.height - 10, { align: 'center' })
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, doc.internal.pageSize.height - 5, { align: 'center' })
      }
      
      // Save the PDF
      let fileName = `Customer_Statement_${statement.customer.name.replace(/[^a-zA-Z0-9]/g, '_')}`
      if (statementStartDate || statementEndDate) {
        if (statementStartDate && statementEndDate) {
          fileName += `_${statementStartDate}_to_${statementEndDate}`
        } else if (statementStartDate) {
          fileName += `_from_${statementStartDate}`
        } else if (statementEndDate) {
          fileName += `_until_${statementEndDate}`
        }
      }
      fileName += `_${new Date().toISOString().split('T')[0]}.pdf`
      
      console.log('Saving PDF:', fileName)
      
      // Enhanced mobile compatibility
      if (isMobile) {
        try {
          // For mobile devices, try multiple approaches
          const pdfBlob = doc.output('blob')
          const url = URL.createObjectURL(pdfBlob)
          
          // Method 1: Try direct download
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          // Method 2: If direct download fails, open in new tab
          setTimeout(() => {
            try {
              window.open(url, '_blank')
            } catch (e) {
              console.log('Failed to open in new tab:', e)
              // Method 3: Show user instructions
              alert('PDF generated successfully! Please use your browser\'s "Save as" option to download the file.')
            }
          }, 1000)
          
          URL.revokeObjectURL(url)
          console.log('PDF saved successfully on mobile')
        } catch (mobileError) {
          console.error('Mobile download failed:', mobileError)
          // Fallback: Try standard save method
          try {
            doc.save(fileName)
            console.log('PDF saved using fallback method')
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError)
            alert('PDF generation completed. Please check your downloads folder or use browser save options.')
          }
        }
      } else {
        // For desktop, use standard save method
        doc.save(fileName)
        console.log('PDF saved successfully on desktop')
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert(`Error generating PDF: ${error.message}`)
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Work Management</h1>
          <p className="text-gray-600">Track work hours and manage payments</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddEntry}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Work Entry</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3 sm:gap-4">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Hours</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{totalHours.toFixed(1)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Paid Amount</p>
              <p className="text-base sm:text-lg font-semibold text-green-600">{formatCurrency(totalPaidAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-base sm:text-lg font-semibold text-red-600">{formatCurrency(pendingAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Betta Amount</p>
              <p className="text-base sm:text-lg font-semibold text-orange-600">{formatCurrency(totalBetaAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Discount Amount</p>
              <p className="text-base sm:text-lg font-semibold text-indigo-600">{formatCurrency(totalDiscountAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Driver Expenses</p>
              <p className="text-base sm:text-lg font-semibold text-yellow-600">{formatCurrency(totalDriverExpenses)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 my-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by customer, vehicle, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input pl-10"
              />
            </div>
          </div>
          
          {/* Payment Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-input text-gray-900"
            >
              <option value="all" className="text-gray-900">All Status</option>
              <option value="pending" className="text-gray-900">Pending</option>
              <option value="partial" className="text-gray-900">Partially Paid</option>
              <option value="paid" className="text-gray-900">Fully Paid</option>
            </select>
          </div>

          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <div className="flex space-x-2">
              <div className="relative flex-1" ref={customerDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
                  className="filter-input text-gray-900 w-full text-left flex items-center justify-between hover:border-brand-orange focus:border-brand-orange transition-colors duration-200"
                >
                  <span>
                    {filterCustomer === 'all' 
                      ? 'All Customers' 
                      : customers.find(c => c.id === filterCustomer)?.name || 'Select Customer'
                    }
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
                
                {isCustomerDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border-2 border-brand-orange rounded-lg shadow-lg max-h-60 overflow-hidden">
                    {/* Search Box */}
                    <div className="p-2 border-b border-brand-orange">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-brand-orange" />
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 text-sm border-2 border-brand-orange rounded-md focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-red transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                    {/* Customer List */}
                    <div className="max-h-48 overflow-y-auto">
                      <button
                        onClick={() => {
                          setFilterCustomer('all')
                          setIsCustomerDropdownOpen(false)
                          setCustomerSearchTerm('')
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors duration-200 ${
                          filterCustomer === 'all' ? 'bg-gradient-to-r from-brand-orange to-brand-red text-white' : 'text-gray-900 hover:text-brand-orange'
                        }`}
                      >
                        All Customers
                      </button>
                      {customers
                        .filter(customer => 
                          customerSearchTerm === '' || 
                          customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
                        )
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(customer => (
                          <button
                            key={customer.id}
                            onClick={() => {
                              setFilterCustomer(customer.id)
                              setIsCustomerDropdownOpen(false)
                              setCustomerSearchTerm('')
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors duration-200 ${
                              filterCustomer === customer.id ? 'bg-gradient-to-r from-brand-orange to-brand-red text-white' : 'text-gray-900 hover:text-brand-orange'
                            }`}
                          >
                            {customer.name}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {customerStatement && (
                <button
                  onClick={() => setIsStatementModalOpen(true)}
                  className="p-2 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200"
                  title={filterCustomer === 'all' ? "View All Customers Statement" : "View Customer Statement"}
                >
                  <FileBarChart className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Vehicle Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
            <select
              value={filterVehicle}
              onChange={(e) => setFilterVehicle(e.target.value)}
              className="filter-input text-gray-900"
            >
              <option value="all" className="text-gray-900">All Vehicles</option>
              {vehicles.map(vehicle => (
                <option key={vehicle.id} value={vehicle.id} className="text-gray-900">
                  {vehicle.registrationNumber} - {vehicle.type}
                </option>
              ))}
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input text-gray-900"
              max={endDate || undefined}
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input text-gray-900"
              min={startDate || undefined}
            />
          </div>
        </div>
        
        {/* Clear Filters Button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              setSearchTerm('')
              setFilterStatus('all')
              setFilterCustomer('all')
              setFilterVehicle('all')
              setCustomerSearchTerm('')
              setIsCustomerDropdownOpen(false)
              setStartDate('')
              setEndDate('')
            }}
            className="px-4 py-2 text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Work Entries List */}
      <div className="space-y-4">
        {filteredEntries.length > 0 ? (
          filteredEntries
            .sort((a, b) => new Date(b.workDate) - new Date(a.workDate))
            .map((entry) => {
              const customer = customers.find(c => c.id === entry.customerId)
              // Get vehicle from work items (first work item's vehicle)
              const firstWorkItem = entry.workItems && entry.workItems.length > 0 ? entry.workItems[0] : null
              const vehicle = vehicles.find(v => v.id === (firstWorkItem?.vehicleId || entry.vehicleId))

              
              return (
                <div key={entry.id} className="card p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {customer?.name || 'Unknown Customer'}
                        </h3>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <User className="h-4 w-4" />
                          <span>{customer?.company || 'N/A'}</span>
                        </div>

                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(entry.workDate).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleGenerateInvoice(entry)}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                        title="Generate Invoice"
                      >
                        <FileText className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEditEntry(entry)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="Edit Entry"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {/* Delete button hidden */}
                      {/* <button
                        onClick={() => openDeleteModal(entry)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete Entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button> */}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 mb-4">
                    {/* 1. Total Hours */}
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <div>
                        <span className="text-sm text-gray-500">Hours:</span>
                        <span className="font-semibold text-blue-600 ml-1">{entry.hoursWorked}</span>
                      </div>
                    </div>

                    {/* 2. Paid Amount */}
                    {calculatePaymentStatus(entry) !== 'pending' && (
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <div>
                          <span className="text-sm text-gray-500">Paid:</span>
                          <span className="font-semibold text-green-600 ml-1">{formatCurrency(entry.paidAmount || 0)}</span>
                        </div>
                      </div>
                    )}

                    {/* 3. Pending Amount */}
                    {(() => {
                      const baseAmount = parseFloat(entry.amount || 0)
                      const betaAmount = entry.workItems ? entry.workItems.reduce((sum, item) => 
                        sum + parseFloat(item.betaAmount || 0), 0) : 0
                      const taxAmount = parseFloat(entry.taxAmount || 0)
                      const totalAmount = baseAmount + betaAmount + taxAmount
                      const paidAmount = parseFloat(entry.paidAmount || 0)
                      const remainingAmount = totalAmount - paidAmount
                      return remainingAmount > 0
                    })() && (
                      <div className="flex items-center space-x-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <div>
                          <span className="text-sm text-gray-500">Pending:</span>
                          <span className="font-semibold text-red-600 ml-1">
                            {(() => {
                              const baseAmount = parseFloat(entry.amount || 0)
                              const betaAmount = entry.workItems ? entry.workItems.reduce((sum, item) => 
                                sum + parseFloat(item.betaAmount || 0), 0) : 0
                              const taxAmount = parseFloat(entry.taxAmount || 0)
                              const totalAmount = baseAmount + betaAmount + taxAmount
                              const paidAmount = parseFloat(entry.paidAmount || 0)
                              const remainingAmount = totalAmount - paidAmount
                              return formatCurrency(remainingAmount)
                            })()}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* 4. Total Amount */}
                    <div className="flex items-center space-x-2">
                      <IndianRupee className="h-4 w-4 text-purple-500" />
                      <div>
                        <span className="text-sm text-gray-500">Total Amount:</span>
                        <span className="font-semibold text-purple-600 ml-1">
                          {formatCurrency((() => {
                            const baseAmount = parseFloat(entry.amount || 0)
                            const betaAmount = entry.workItems ? entry.workItems.reduce((sum, item) => 
                              sum + parseFloat(item.betaAmount || 0), 0) : 0
                            const taxAmount = parseFloat(entry.taxAmount || 0)
                            return baseAmount + betaAmount + taxAmount
                          })())}
                        </span>
                      </div>
                    </div>

                    {/* 5. Betta Amount */}
                    {(() => {
                      const totalBetaAmount = entry.workItems ? entry.workItems.reduce((sum, item) => 
                        sum + parseFloat(item.betaAmount || 0), 0) : 0
                      return totalBetaAmount > 0
                    })() && (
                      <div className="flex items-center space-x-2">
                        <IndianRupee className="h-4 w-4 text-orange-500" />
                        <div>
                          <span className="text-sm text-gray-500">Betta Amount:</span>
                          <span className="font-semibold text-orange-600 ml-1">
                            {formatCurrency(entry.workItems ? entry.workItems.reduce((sum, item) => 
                              sum + parseFloat(item.betaAmount || 0), 0) : 0)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* 6. Discount Amount */}
                    {entry.discountAmount && parseFloat(entry.discountAmount) > 0 && (
                      <div className="flex items-center space-x-2">
                        <IndianRupee className="h-4 w-4 text-indigo-500" />
                        <div>
                          <span className="text-sm text-gray-500">Discount:</span>
                          <span className="font-semibold text-indigo-600 ml-1">{formatCurrency(entry.discountAmount)}</span>
                        </div>
                      </div>
                    )}

                    {/* 7. Driver Expenses */}
                    {(() => {
                      const totalDriverExpenses = entry.workItems ? entry.workItems.reduce((sum, item) => 
                        sum + parseFloat(item.driverExpenses || 0), 0) : 0
                      return totalDriverExpenses > 0
                    })() && (
                      <div className="flex items-center space-x-2">
                        <IndianRupee className="h-4 w-4 text-yellow-500" />
                        <div>
                          <span className="text-sm text-gray-500">Driver Expenses:</span>
                          <span className="font-semibold text-yellow-600 ml-1">
                            {formatCurrency(entry.workItems ? entry.workItems.reduce((sum, item) => 
                              sum + parseFloat(item.driverExpenses || 0), 0) : 0)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Location */}
                    {entry.location && (
                      <div className="col-span-full">
                        <span className="text-sm text-gray-500">Location:</span>
                        <span className="font-medium text-gray-900 ml-1">{entry.location}</span>
                      </div>
                    )}

                    {/* Invoice Information - Only Invoice Number */}
                    {entry.invoiceNumber && (
                      <div className="col-span-full mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium text-purple-900">Invoice Details:</h5>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(calculatePaymentStatus(entry))}`}>
                            {(() => {
                              const status = calculatePaymentStatus(entry)
                              switch (status) {
                                case 'paid':
                                  return 'FULLY PAID'
                                case 'partial':
                                  return 'PARTIALLY PAID'
                                default:
                                  return 'PENDING'
                              }
                            })()}
                          </span>
                        </div>
                        <div className="text-xs">
                          <div className="break-words overflow-wrap-anywhere">
                            <span className="text-purple-600 font-medium">Invoice:</span>
                            <span className="ml-1 text-gray-700 break-all">{entry.invoiceNumber}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>



                  {entry.notes && (
                    <div className="pt-4 border-t border-gray-100">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Notes:</h4>
                      <p className="text-sm text-gray-600">{entry.notes}</p>
                    </div>
                  )}

                  {entry.paymentStatus === 'paid' && entry.paidDate && (
                    <div className="pt-4 border-t border-gray-100 text-xs text-green-600">
                      Paid on: {new Date(entry.paidDate).toLocaleDateString()}
                    </div>
                  )}


                </div>
              )
            })
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4">
              <Search className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? `No results found for "${searchTerm}"` : 'No work entries found'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? `Try searching for: customer names, vehicle numbers, locations, work descriptions, or invoice numbers`
                : filterStatus !== 'all' || filterCustomer !== 'all' || filterVehicle !== 'all' || startDate || endDate
                ? 'Try adjusting your filter criteria'
                : 'Get started by adding your first work entry'
              }
            </p>
            {searchTerm && (
              <div className="space-y-2">
                <button
                  onClick={() => setSearchTerm('')}
                  className="btn btn-secondary mr-2"
                >
                  Clear Search
                </button>
                <button
                  onClick={() => {
                    setSearchTerm('')
                    setFilterStatus('all')
                    setFilterCustomer('all')
                    setFilterVehicle('all')
                    setCustomerSearchTerm('')
                    setIsCustomerDropdownOpen(false)
                    setStartDate('')
                    setEndDate('')
                  }}
                  className="btn btn-primary"
                >
                  Clear All Filters
                </button>
              </div>
            )}
            {!searchTerm && filterStatus === 'all' && filterCustomer === 'all' && filterVehicle === 'all' && !startDate && !endDate && (
              <button
                onClick={handleAddEntry}
                className="btn btn-primary"
              >
                Add Your First Work Entry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Work Entry Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingEntry ? 'Edit Work Entry' : 'Add New Work Entry'}
        size="max-w-4xl"
      >
        <WorkEntryForm
          workEntry={editingEntry}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isSubmitting={isSubmitting}
        />
      </Modal>

      {/* Invoice Generator Modal */}
      <Modal
        isOpen={isInvoiceModalOpen}
        onClose={handleCloseInvoiceModal}
        title="Generate Invoice"
        size="max-w-4xl"
      >
        {invoiceEntry && (
          <InvoiceGenerator
            workEntry={invoiceEntry}
            customer={customers.find(c => c.id === invoiceEntry.customerId)}
            vehicle={vehicles.find(v => v.id === invoiceEntry.vehicleId)}
            onClose={handleCloseInvoiceModal}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Work Entry"
        message={`Are you sure you want to delete this work entry?`}
        itemName={`Work Entry for ${deleteModal.entry ? customers.find(c => c.id === deleteModal.entry.customerId)?.name : 'Customer'}`}
      />

      {/* Customer Statement Modal */}
      <Modal
        isOpen={isStatementModalOpen}
        onClose={() => {
          setIsStatementModalOpen(false)
          setStatementStartDate('')
          setStatementEndDate('')
          setStatementPaymentStatus('all')
        }}
        title={`${filterCustomer === 'all' ? 'All Customers' : 'Customer'} Statement - ${customerStatement?.customer?.name || 'Customer'}`}
        size="max-w-6xl"
      >
        {customerStatement && (
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-3">
                <User className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{customerStatement.customer.name}</h3>
                  <p className="text-sm text-gray-600">{customerStatement.customer.company || 'N/A'}</p>
                  {filterCustomer !== 'all' && <p className="text-sm text-gray-600">{customerStatement.customer.phone || 'N/A'}</p>}
                </div>
              </div>
            </div>

            {/* Statement Filters */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Statement Filters</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={statementStartDate}
                    onChange={(e) => setStatementStartDate(e.target.value)}
                    className="input"
                    max={statementEndDate || undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={statementEndDate}
                    onChange={(e) => setStatementEndDate(e.target.value)}
                    className="input"
                    min={statementStartDate || undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                  <select
                    value={statementPaymentStatus}
                    onChange={(e) => setStatementPaymentStatus(e.target.value)}
                    className="input"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {statementStartDate || statementEndDate || statementPaymentStatus !== 'all' ? (
                    <>
                      Showing {statementPaymentStatus !== 'all' ? statementPaymentStatus : 'all'} records
                      {statementStartDate || statementEndDate ? (
                        <>
                          {' '}from{' '}
                          {statementStartDate ? new Date(statementStartDate).toLocaleDateString() : 'beginning'} to{' '}
                          {statementEndDate ? new Date(statementEndDate).toLocaleDateString() : 'end'}
                        </>
                      ) : ''}
                    </>
                  ) : (
                    `Showing all records for ${filterCustomer === 'all' ? 'all customers' : 'this customer'}`
                  )}
                </p>
                <button
                  onClick={() => {
                    setStatementStartDate('')
                    setStatementEndDate('')
                    setStatementPaymentStatus('all')
                  }}
                  className="px-3 py-1 text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FileBarChart className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Records</p>
                    <p className="text-xl font-semibold text-gray-900">{customerStatement.totalRecords}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-xl font-semibold text-green-600">{formatCurrency(customerStatement.totalAmount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Given Amount</p>
                    <p className="text-xl font-semibold text-orange-600">{formatCurrency(customerStatement.totalPaidAmount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pending Amount</p>
                    <p className="text-xl font-semibold text-red-600">{formatCurrency(customerStatement.pendingAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Clock className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Hours</p>
                    <p className="text-xl font-semibold text-purple-600">{customerStatement.totalHours.toFixed(1)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Base Amount</p>
                    <p className="text-xl font-semibold text-indigo-600">{formatCurrency(customerStatement.totalBaseAmount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Betta Amount</p>
                    <p className="text-xl font-semibold text-yellow-600">{formatCurrency(customerStatement.totalBetaAmount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="bg-teal-100 p-2 rounded-lg">
                    <IndianRupee className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tax Amount</p>
                    <p className="text-xl font-semibold text-teal-600">{formatCurrency(customerStatement.totalTaxAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Work Items Details */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900">Work Items Details</h4>
                <p className="text-sm text-gray-600">Detailed breakdown of all work items for this customer</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Betta Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Given Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customerStatement.allWorkItems.map((item, index) => {
                      const vehicle = vehicles.find(v => v.id === item.vehicleId)
                      // Calculate total amount for this work item
                      const baseAmount = parseFloat(item.amount || 0)
                      const betaAmount = parseFloat(item.betaAmount || 0)
                      const totalAmount = baseAmount + betaAmount
                      
                      // Find the corresponding work entry to get the given amount
                      const workEntry = customerStatement.customerEntries.find(entry => 
                        entry.workItems && entry.workItems.some(workItem => 
                          workItem.vehicleId === item.vehicleId && 
                          workItem.description === item.description &&
                          workItem.hoursWorked === item.hoursWorked
                        )
                      )
                      
                      // Calculate given amount proportionally for this work item
                      let givenAmount = calculateProportionalGivenAmount(item, workEntry)
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {new Date(item.workDate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div>
                              <div className="font-medium">{item.workDescription || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{item.description || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.location || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {vehicle ? `${vehicle.registrationNumber} - ${vehicle.type}` : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.hoursWorked || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(item.amount || 0)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(item.betaAmount || 0)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(totalAmount)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(givenAmount)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Summary</h4>
                <button
                  onClick={() => generatePDF(customerStatement)}
                  disabled={isGeneratingPDF}
                  className={`flex items-center space-x-2 px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium touch-manipulation ${isGeneratingPDF ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isGeneratingPDF ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Generating PDF...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <span>Download PDF</span>
                    </>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Records:</span>
                  <span className="ml-2 font-semibold text-gray-900">{customerStatement.totalRecords}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="ml-2 font-semibold text-green-600">{formatCurrency(customerStatement.totalAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Given Amount:</span>
                  <span className="ml-2 font-semibold text-orange-600">{formatCurrency(customerStatement.totalPaidAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Pending Amount:</span>
                  <span className="ml-2 font-semibold text-red-600">{formatCurrency(customerStatement.pendingAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Hours:</span>
                  <span className="ml-2 font-semibold text-gray-900">{customerStatement.totalHours.toFixed(1)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Discount Amount:</span>
                  <span className="ml-2 font-semibold text-gray-900">{formatCurrency(customerStatement.totalDiscountAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

    </div>
  )
}

export default WorkManagement
